import { useEffect, useRef, useState } from 'react';

interface PullToRefreshOptions {
  /**
   * Threshold in pixels to trigger refresh
   */
  threshold?: number;
  
  /**
   * Maximum distance the pull indicator can be pulled
   */
  maxDistance?: number;
  
  /**
   * Function to call when refresh is triggered
   */
  onRefresh: () => Promise<void>;
  
  /**
   * Element to attach the pull-to-refresh functionality to
   */
  targetRef: React.RefObject<HTMLElement>;
  
  /**
   * Whether pull-to-refresh is disabled
   */
  disabled?: boolean;
  
  /**
   * Whether the refresh is in progress
   */
  isRefreshing?: boolean;
}

interface PullToRefreshState {
  /**
   * Whether the pull-to-refresh is currently being pulled
   */
  isPulling: boolean;
  
  /**
   * Current pulled distance
   */
  pullDistance: number;
  
  /**
   * Whether the pull distance has exceeded the threshold
   */
  thresholdExceeded: boolean;
  
  /**
   * Attach this ref to the pull indicator element
   */
  indicatorRef: React.RefObject<HTMLDivElement>;
}

/**
 * Hook to implement pull-to-refresh functionality for mobile devices
 */
export function usePullToRefresh({
  threshold = 80,
  maxDistance = 120,
  onRefresh,
  targetRef,
  disabled = false,
  isRefreshing = false,
}: PullToRefreshOptions): PullToRefreshState {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [thresholdExceeded, setThresholdExceeded] = useState(false);
  const indicatorRef = useRef<HTMLDivElement>(null);
  
  const startY = useRef(0);
  const currentY = useRef(0);
  const isActive = useRef(false);
  
  useEffect(() => {
    if (!targetRef.current || disabled) return;
    
    const element = targetRef.current;
    
    const handleTouchStart = (e: TouchEvent) => {
      // Only activate when scrolled to the top
      if (element.scrollTop <= 0) {
        isActive.current = true;
        startY.current = e.touches[0].clientY;
        currentY.current = startY.current;
        
        setIsPulling(true);
      }
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (!isActive.current) return;
      
      currentY.current = e.touches[0].clientY;
      const deltaY = currentY.current - startY.current;
      
      // Only allow pulling down
      if (deltaY > 0) {
        // Resistance factor - the pull distance is less than the actual finger movement
        const resistance = 0.4;
        const distance = Math.min(deltaY * resistance, maxDistance);
        
        setPullDistance(distance);
        setThresholdExceeded(distance >= threshold);
        
        // Prevent default scrolling behavior while pulling
        e.preventDefault();
      } else {
        // Reset if user pulls up
        setPullDistance(0);
        setThresholdExceeded(false);
      }
    };
    
    const handleTouchEnd = async () => {
      if (!isActive.current) return;
      
      isActive.current = false;
      
      if (thresholdExceeded && !isRefreshing) {
        try {
          await onRefresh();
        } catch (error) {
          console.error('Pull to refresh error:', error);
        }
      }
      
      // Reset pull state
      setPullDistance(0);
      setThresholdExceeded(false);
      setIsPulling(false);
    };
    
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('touchcancel', handleTouchEnd);
    
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [targetRef, onRefresh, threshold, maxDistance, disabled, thresholdExceeded, isRefreshing]);
  
  return {
    isPulling,
    pullDistance,
    thresholdExceeded,
    indicatorRef,
  };
} 